import { queryKeys } from '../../../config/queryKeys'
import { usePagedList } from '../../../hooks/usePagedList'
import { useLoadPastHiddenPage } from '../../../hooks/useLoadPastHiddenPage'
import { getSpareCandidates } from '../../../services/game/spareCandidatesService'
import { withoutAskedOrRosterPlayers } from '../rosterExclusion'
import type { SpareCandidate, SpareCandidateFilters } from '../types'

/**
 * How long a fetched candidate list stays fresh. Short, because the list is
 * time-sensitive: another official can ask a spare, or a player can change
 * availability, while the finder is open. Long enough that reopening the finder
 * or flipping back to a previous filter doesn't refetch immediately.
 */
const STALE_TIME = 20 * 1000

interface SpareCandidatesOptions {
  /** False while the finder is closed or the game record is still loading. */
  enabled: boolean
  /** This game's roster — never listed. */
  gameRosterPlayerIds: readonly string[]
}

export interface SpareCandidates {
  /** The listed candidates: every loaded page, minus asked and rostered players. */
  candidates: SpareCandidate[]
  /** Nothing to show yet — including while hidden-only pages are skipped. */
  isLoading: boolean
  isError: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  /** The last next-page load failed; automatic loading has stopped. */
  nextPageFailed: boolean
  /** Load the next page (scroll reached the end). */
  fetchNextPage: () => void
  /** Reload from page one (pull-to-refresh). */
  refetch: () => Promise<unknown>
}

/**
 * SN-8309 — the Player Finder list for a game and team, shared by web and
 * mobile: GET /games/:id/spare-candidates, paged as the user scrolls, keyed per
 * game AND team so switching teams never shows the other team's list.
 *
 * Anyone already asked for this game, or already on its roster, is left out —
 * they're followed up on the attendance screen. Because that hiding happens
 * here, a page can add nothing to the screen; the list then loads on by itself
 * rather than stall on an empty screen while pages remain.
 *
 * A send or a withdraw invalidates `spareCandidates.all`, which resets the list
 * to a fresh page one (usePagedList), so an asked player drops off whichever
 * page they were on.
 */
export const useSpareCandidates = (
  {
    gameId,
    teamId,
    date,
    positionId,
    genderId,
    location,
  }: SpareCandidateFilters,
  { enabled, gameRosterPlayerIds }: SpareCandidatesOptions
): SpareCandidates => {
  'use memo'

  const list = usePagedList<SpareCandidate>({
    queryKey: queryKeys.spareCandidates.byGame(
      gameId,
      teamId,
      date,
      positionId,
      genderId,
      location
    ),
    fetchPage: (page) =>
      getSpareCandidates({
        gameId,
        teamId,
        date,
        positionId,
        genderId,
        location,
        page,
      }),
    enabled: enabled && Boolean(gameId && teamId),
    staleTime: STALE_TIME,
  })

  const candidates = withoutAskedOrRosterPlayers(
    list.items,
    gameRosterPlayerIds
  )

  useLoadPastHiddenPage({
    lastPageIds: list.lastPageItems.map((candidate) => candidate.id),
    visibleIds: new Set(candidates.map((candidate) => candidate.id)),
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    nextPageFailed: list.nextPageFailed,
    fetchNextPage: list.fetchNextPage,
  })

  // An empty screen with pages still to come is loading, not "no results".
  const isAwaitingMore =
    candidates.length === 0 &&
    (list.isFetchingNextPage || (list.hasNextPage && !list.nextPageFailed))

  return {
    candidates,
    isLoading: list.isLoading || isAwaitingMore,
    isError: list.isError,
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    nextPageFailed: list.nextPageFailed,
    fetchNextPage: list.fetchNextPage,
    refetch: list.refetch,
  }
}

export default useSpareCandidates
